import React, { useRef } from 'react';
import { useEnteredView } from '@fiverr-private/hooks';
import { I18n } from '@fiverr-private/i18n-react';
import { Container } from '@fiverr-private/layout_components';
import { useLocalizationContext } from '@fiverr-private/seller_pages_toolbox';
import { Tooltip } from '@fiverr-private/tooltip';
import { Text } from '@fiverr-private/typography';
import { useSellerRole } from '../../../hooks/useSellerRole';
import { useBI } from '../../BIContext';

const MIN_RANGE_DIFFERENCE = 200;

interface OrderPriceProps {
    sellerRoleId?: string;
}

export const OrderPrice: React.FC<OrderPriceProps> = ({ sellerRoleId }) => {
    const observableRef = useRef<HTMLDivElement>(null);
    const sellerRole = useSellerRole(sellerRoleId);
    const { forceRoundCurrencyFormatFromUSD } = useLocalizationContext();
    const { reportSellerHighlightsPriceRangeImpression } = useBI();

    useEnteredView(observableRef, {
        threshold: 1,
        onEntered: () => {
            reportSellerHighlightsPriceRangeImpression({ label });
        },
    });

    if (!sellerRole || !sellerRole.startingPrice) {
        return null;
    }

    const orderPriceFrom = forceRoundCurrencyFormatFromUSD(sellerRole.startingPrice);
    const orderPriceTo = sellerRole.highestPrice && forceRoundCurrencyFormatFromUSD(sellerRole.highestPrice);
    const isRange =
        !!sellerRole.highestPrice && sellerRole.highestPrice - sellerRole.startingPrice >= MIN_RANGE_DIFFERENCE;
    const label = isRange ? `${orderPriceFrom}-${orderPriceTo}` : orderPriceFrom;

    return (
        <Container ref={observableRef}>
            <Tooltip content={<I18n k="seller_page_perseus.professions_page.highlights.typical_order_price_tooltip" />}>
                <Text
                    size={{
                        default: 'b_lg',
                        md: 'b_xl',
                        lg: 'b_xxl',
                    }}
                    fontWeight="bold"
                    marginBottom="1"
                    whiteSpace="nowrap"
                >
                    {label}
                </Text>
                <Text size="b_sm" fontWeight="semibold" color="bodySecondary" whiteSpace="nowrap">
                    {isRange ? (
                        <I18n k="seller_page_perseus.professions_page.highlights.typical_order_price" />
                    ) : (
                        <I18n k="seller_page_perseus.professions_page.highlights.starting_price" />
                    )}
                </Text>
            </Tooltip>
        </Container>
    );
};
